<script setup lang="ts">
import { type VNode } from 'vue';
import { NxModal } from '../Modal';

/**
 * The kit's standard dialog: a title, an optional description and the buttons.
 * The description is the modal's subtitle in the head (I-2949): one muted line
 * under the title that never scrolls. The body is a scroll container since the
 * dialog can be sized (I-2799), and a paragraph pulled up into the head with a
 * negative margin was cut off at its top there.
 */
const props = withDefaults(
  defineProps<{
    title: string;
    description?: string;
    /** Initial body width; a user-chosen size always takes precedence. */
    size?: 'default' | 'wide' | 'xwide' | 'xxwide' | 'viewport';
    autoWidth?: boolean;
    /** Hand the available height to a bounded editor, list or other flexible content. */
    fill?: boolean;
    /** Disable sizing for a compact confirmation or name prompt. */
    resizable?: boolean;
    /** Remember the chosen size on the host's settings path. */
    storageKey?: string;
  }>(),
  { resizable: true }
);
const open = defineModel<boolean>('open', { default: false });

const emit = defineEmits<{
  /** Immediately on close */
  (event: 'close'): void;
  /** Once the dialog has been shown — the fade-in may still be running */
  (event: 'modalOpened'): void;
  /** Once the dialog has been closed — the fade-out may still be running */
  (event: 'modalClosed'): void;
}>();

const slots = defineSlots<{
  /** The item that triggers the dialog. */
  trigger?: () => VNode;
  /** The dialog content. */
  content?: () => VNode;
  /** The dialog buttons. */
  buttons?: (props: { close: () => void }) => VNode;
}>();

defineOptions({ inheritAttrs: false });
</script>

<template>
  <div v-if="slots.trigger" class="trigger" v-bind="$attrs" @click="() => (open = true)">
    <slot name="trigger"></slot>
  </div>
  <NxModal
    v-model:open="open"
    :title="props.title"
    :subtitle="props.description"
    :size="props.size"
    :auto-width="props.autoWidth"
    :fill="props.fill"
    :resizable="props.resizable"
    :storage-key="props.storageKey"
    @close="emit('close')"
    @modal-closed="emit('modalClosed')"
    @modal-opened="emit('modalOpened')"
  >
    <template v-if="slots.content" #body>
      <slot name="content"></slot>
    </template>
    <template #footer="{ close }">
      <slot name="buttons" :close="close"></slot>
    </template>
  </NxModal>
</template>

<style scoped>
.trigger {
  width: fit-content;
  height: fit-content;
}
</style>
